import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const MAX_FILES = 200;

type MatchStatus = 'matched' | 'unmatched';
type UpdateStatus = 'pending' | 'updating' | 'done' | 'error';

type WalletShopMediaAsset = {
  Type: string;
  Url: string;
  MimeType?: string;
};

type WalletShopProduct = {
  ProductKey: string;
  Name: string;
  Environment?: 'dev' | 'prod';
  MediaAssets?: WalletShopMediaAsset[];
  [key: string]: any;
};

type StickerFileEntry = {
  id: string;
  file: File;
  derivedKey: string;
  previewUrl: string;
  matchStatus: MatchStatus;
  matchedProduct?: WalletShopProduct;
  updateStatus: UpdateStatus;
  error?: string;
};

// Same derivation BulkStickerUpload uses when creating stickers, so filenames line up
// with the ProductKey already stored for that sticker.
const fileNameToKey = (filename: string): string =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toUpperCase();

const BulkStickerThumbnailUpdate: React.FC = () => {
  const navigate = useNavigate();

  const [existingProducts, setExistingProducts] = useState<WalletShopProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [entries, setEntries] = useState<StickerFileEntry[]>([]);
  const [updating, setUpdating] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const loadExistingStickers = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) {
      setLoadingProducts(false);
      return;
    }
    setLoadingProducts(true);
    try {
      const res = await fetch(`${walletApiBase}/getWalletShopProductsV2`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          UserID: 'dashboard',
          CategoryKey: 'STICKER',
          IncludeInactive: true,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load existing stickers');
      setExistingProducts((json.data?.products as WalletShopProduct[]) || []);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to load existing stickers');
    } finally {
      setLoadingProducts(false);
    }
  }, [headerWithAuth]);

  useEffect(() => {
    loadExistingStickers();
  }, [loadExistingStickers]);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      entries.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (!selected.length) return;

    const remaining = MAX_FILES - entries.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }

    const capped = selected.slice(0, remaining);
    if (capped.length < selected.length) {
      toast.warn(`Only ${capped.length} of ${selected.length} files added (${MAX_FILES} max).`);
    }

    const productsByKey = new Map(existingProducts.map((p) => [p.ProductKey, p]));

    const newEntries: StickerFileEntry[] = capped.map((file) => {
      const derivedKey = fileNameToKey(file.name);
      const matchedProduct = productsByKey.get(derivedKey);
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
        file,
        derivedKey,
        previewUrl: URL.createObjectURL(file),
        matchStatus: matchedProduct ? 'matched' : 'unmatched',
        matchedProduct,
        updateStatus: 'pending',
      };
    });

    setEntries((prev) => [...prev, ...newEntries]);
    e.target.value = '';
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => {
      const target = prev.find((entry) => entry.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const matchedEntries = entries.filter((entry) => entry.matchStatus === 'matched');
  const unmatchedEntries = entries.filter((entry) => entry.matchStatus === 'unmatched');

  const handleUpdateAll = async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    const pending = matchedEntries.filter(
      (entry) => entry.updateStatus === 'pending' || entry.updateStatus === 'error'
    );
    if (!pending.length) {
      toast.info('No matched stickers ready to update.');
      return;
    }

    setUpdating(true);
    setCompletedCount(0);
    let doneCount = 0;

    for (const entry of pending) {
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, updateStatus: 'updating', error: undefined } : e))
      );

      try {
        const product = entry.matchedProduct;
        if (!product) throw new Error('No matched product');

        // Step 1: get a presigned URL for the new webp thumbnail, keyed to the same ProductKey.
        const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ProductKey: product.ProductKey,
            ContentType: entry.file.type || 'image/webp',
            AssetType: 'thumbnail',
          }),
        });
        const presignedJson = await presignedRes.json();
        if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

        const uploadData = presignedJson.data;

        // Step 2: upload the webp file directly to storage.
        const uploadRes = await fetch(uploadData.UploadUrl, {
          method: 'PUT',
          body: await entry.file.arrayBuffer(),
        });
        if (!uploadRes.ok) throw new Error('Failed to upload image to storage');

        const assetUrl: string = uploadData.AssetUrl;

        // Step 3: replace the thumbnail entry in MediaAssets, keep every other asset the
        // product already has, then resend the full MediaAssets array — updateWalletShopProduct
        // replaces MediaAssets wholesale rather than merging it.
        const existingAssets = product.MediaAssets || [];
        const nextAssets: WalletShopMediaAsset[] = existingAssets.some((a) => a.Type === 'thumbnail')
          ? existingAssets.map((a) =>
              a.Type === 'thumbnail'
                ? { Type: 'thumbnail', Url: assetUrl, MimeType: entry.file.type || 'image/webp' }
                : a
            )
          : [...existingAssets, { Type: 'thumbnail', Url: assetUrl, MimeType: entry.file.type || 'image/webp' }];

        const updateRes = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ProductKey: product.ProductKey,
            Environment: product.Environment || 'prod',
            MediaAssets: nextAssets,
          }),
        });
        const updateJson = await updateRes.json();
        if (!updateRes.ok) throw new Error(updateJson.message || 'Failed to update product');

        doneCount++;
        setCompletedCount(doneCount);
        setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, updateStatus: 'done' } : e)));
      } catch (err) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === entry.id
              ? { ...e, updateStatus: 'error', error: (err as Error).message || 'Update failed' }
              : e
          )
        );
      }
    }

    setUpdating(false);

    const failedCount = pending.length - doneCount;
    if (doneCount > 0 && failedCount === 0) {
      toast.success(`All ${doneCount} sticker thumbnail(s) updated successfully!`);
    } else if (doneCount > 0) {
      toast.warn(`${doneCount} updated, ${failedCount} failed. See details below for which ones and why.`);
    } else {
      toast.error('All updates failed. See details below for why.');
    }
  };

  const pendingCount = matchedEntries.filter(
    (entry) => entry.updateStatus === 'pending' || entry.updateStatus === 'error'
  ).length;
  const doneCount = entries.filter((entry) => entry.updateStatus === 'done').length;

  const statusBadge = (entry: StickerFileEntry) => {
    if (entry.matchStatus === 'unmatched') {
      return <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-500/80 text-white'>No match</span>;
    }
    if (entry.updateStatus === 'done') {
      return <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Done</span>;
    }
    if (entry.updateStatus === 'updating') {
      return <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Updating…</span>;
    }
    if (entry.updateStatus === 'error') {
      return <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>;
    }
    return <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-500/80 text-white'>Matched</span>;
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Bulk Update Sticker Thumbnails</h1>
            <p className='text-sm text-gray-400'>
              Select WebP files whose names match existing sticker ProductKeys (e.g. GOLDEN_HEART.webp) to replace
              their PNG thumbnails.
            </p>
          </div>
          <button
            onClick={() => navigate('/products?category=GIFT')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20'
          >
            Back to Products
          </button>
        </div>

        {loadingProducts ? (
          <div className='text-sm text-gray-400'>Loading existing stickers...</div>
        ) : (
          <>
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <h2 className='text-lg font-semibold text-white'>WebP Files</h2>
                  <p className='text-xs text-gray-400 mt-0.5'>
                    {existingProducts.length} existing sticker(s) loaded · {entries.length} / {MAX_FILES} files
                    added
                    {doneCount > 0 && ` · ${doneCount} updated`}
                  </p>
                </div>
                <label className='cursor-pointer px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'>
                  + Add WebP Files
                  <input
                    type='file'
                    accept='image/webp'
                    multiple
                    className='hidden'
                    onChange={handleFilesSelected}
                    disabled={updating || entries.length >= MAX_FILES}
                  />
                </label>
              </div>

              {entries.length === 0 ? (
                <div className='border-2 border-dashed border-white/10 rounded-xl py-16 flex flex-col items-center justify-center gap-3'>
                  <p className='text-sm text-gray-400'>No files selected yet.</p>
                  <label className='cursor-pointer px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'>
                    Select WebP Thumbnails
                    <input
                      type='file'
                      accept='image/webp'
                      multiple
                      className='hidden'
                      onChange={handleFilesSelected}
                      disabled={updating}
                    />
                  </label>
                  <p className='text-xs text-gray-500'>Up to {MAX_FILES} files · WebP only</p>
                </div>
              ) : (
                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'>
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className={`relative bg-white/5 border rounded-xl overflow-hidden flex flex-col ${
                        entry.matchStatus === 'unmatched'
                          ? 'border-gray-500/40'
                          : entry.updateStatus === 'done'
                          ? 'border-emerald-400/40'
                          : entry.updateStatus === 'error'
                          ? 'border-red-400/40'
                          : entry.updateStatus === 'updating'
                          ? 'border-amber-400/40'
                          : 'border-white/10'
                      }`}
                    >
                      <div className='absolute top-2 left-2 z-10'>{statusBadge(entry)}</div>

                      {entry.updateStatus !== 'updating' && (
                        <button
                          onClick={() => removeEntry(entry.id)}
                          className='absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-black/50 text-gray-300 hover:text-white text-xs flex items-center justify-center'
                        >
                          ✕
                        </button>
                      )}

                      <div className='h-28 bg-white/10 flex items-center justify-center overflow-hidden'>
                        <img src={entry.previewUrl} alt={entry.derivedKey} className='w-full h-full object-contain p-1' />
                      </div>

                      <div className='p-2 space-y-1 flex-1'>
                        <p className='text-[10px] text-gray-300 font-mono truncate'>{entry.derivedKey}</p>
                        {entry.matchStatus === 'unmatched' ? (
                          <p className='text-[10px] text-gray-400 leading-tight'>
                            No sticker with this ProductKey exists — it will be skipped.
                          </p>
                        ) : (
                          <p className='text-[10px] text-gray-400 truncate'>{entry.matchedProduct?.Name}</p>
                        )}
                        {entry.error && <p className='text-[10px] text-red-300 leading-tight'>{entry.error}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {entries.length > 0 && (
              <div className='flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-6 py-4'>
                <div className='text-sm text-gray-300'>
                  {updating
                    ? `Updating… ${completedCount} / ${pendingCount + completedCount} done`
                    : `${pendingCount} matched sticker(s) ready to update${
                        unmatchedEntries.length > 0 ? ` · ${unmatchedEntries.length} unmatched (will be skipped)` : ''
                      }`}
                </div>
                <button
                  onClick={handleUpdateAll}
                  disabled={updating || pendingCount === 0}
                  className='px-6 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-50'
                >
                  {updating ? 'Updating…' : `Update ${pendingCount} Thumbnail${pendingCount !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}

            {!updating && entries.length > 0 && (unmatchedEntries.length > 0 || entries.some((e) => e.updateStatus === 'error')) && (
              <div className='bg-amber-500/10 border border-amber-400/40 rounded-2xl px-6 py-4 space-y-2'>
                <h3 className='text-sm font-semibold text-amber-200'>Not updated</h3>
                <ul className='text-xs text-amber-100/90 space-y-1'>
                  {unmatchedEntries.map((entry) => (
                    <li key={entry.id}>
                      <span className='font-mono'>{entry.derivedKey}</span> — no existing sticker with this
                      ProductKey was found.
                    </li>
                  ))}
                  {entries
                    .filter((entry) => entry.updateStatus === 'error')
                    .map((entry) => (
                      <li key={entry.id}>
                        <span className='font-mono'>{entry.derivedKey}</span> — {entry.error || 'update failed'}.
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BulkStickerThumbnailUpdate;
